import { toast } from "react-toastify";
import FileSaver from "file-saver";
import apiClient from "../../apis/api-client";
import { default as tenantManagementClient } from "../../apis/tenant-management/tenant-management-client";
import { rebuildQuery } from "../../../utils/url-utils";
import { ResourceLoadingState } from "../smart-apply/procedure-detail/resource-loading-state";
import { TabState } from "../smart-apply/procedure-detail/tab-state";
import {
  ProcedureDetailType,
  ProcedureEntity,
  ProcedureType,
  PublishSetting,
} from "../../domain/procedures/procedure-entity";
import {
  SmartApplyApiError,
  reportAsyncError,
  reportUnexpectedError,
  BaseApplicationService,
  ToastMessage,
} from "@graffer-inc/smart-apply";
import { OrgWithUsingFeatures } from "../../domain/organization/org-with-using-features";
import { Procedure } from "../../domain/proc-management/core/types";
import {
  isNotFoundError,
  isPermissionError,
  isSmartApplyAccessRestrictionError,
  reportProcManagementRelatedError,
} from "../../domain/proc-management/util/error";
import { buildSmartApplyProcedureUrlPath } from "../../domain/proc-management/util";
import { Group } from "../../domain/group/group";
import { gqlClient, gqlHeader, runGqlQueryV2 } from "@graffer-inc/smart-apply";
import { CustomPathSettings } from "../../domain/procedures/custom-path";
import * as client from "../../../graphql/client";
import { ProcedurePresentation } from "@graffer-inc/cloud-gov-core";

export type ProcedureContextProps = {
  isSubmitting: boolean;
  resourceLoadState: ResourceLoadingState;
  tabState: TabState;
  procedure?: ProcedureEntity;
  customPathSettings: CustomPathSettings;
  orgWithUsingFeature?: OrgWithUsingFeatures;
  orgSlug: string;
  procMsts?: Pick<Procedure, "id" | "name">[];
  groups?: Group[];
  hasCustomUIDoorPage: boolean;
  switchTab: typeof ProcedureDetailService.prototype.switchTab;
  resetState: typeof ProcedureDetailService.prototype.resetState;

  onDraftUpdate: typeof ProcedureDetailService.prototype.onDraftUpdate;
  onGrafferManagedChanged: typeof ProcedureDetailService.prototype.onGrafferManagedChanged;
  onGroupAdded: typeof ProcedureDetailService.prototype.onGroupAdded;
  onPublish: typeof ProcedureDetailService.prototype.onPublish;
  onUnPublish: typeof ProcedureDetailService.prototype.onUnPublish;
  onCopyCreate: typeof ProcedureDetailService.prototype.onCopyCreate;
  onProcedureDelete: typeof ProcedureDetailService.prototype.onProcedureDelete;
  onProcedureUpdate: typeof ProcedureDetailService.prototype.onProcedureUpdate;
  onExportProcedure: typeof ProcedureDetailService.prototype.onExportProcedure;
  onUpdateCustomerDlConfig: typeof ProcedureDetailService.prototype.onUpdateCustomerDlConfig;
  onArchive: typeof ProcedureDetailService.prototype.onArchive;
  onUnarchive: typeof ProcedureDetailService.prototype.onUnarchive;

  fetchProcMstBySmartApply: typeof ProcedureDetailService.prototype.fetchProcMstBySmartApply;
  onReturnToDraft: typeof ProcedureDetailService.prototype.onReturnToDraft;
  onUpdateProcMaster: typeof ProcedureDetailService.prototype.onUpdateProcMaster;
  onPublishSettingUpdate: typeof ProcedureDetailService.prototype.onPublishSettingUpdate;
  onResumeAccepting: typeof ProcedureDetailService.prototype.onResumeAccepting;
  onPauseAccepting: typeof ProcedureDetailService.prototype.onPauseAccepting;
  isOrgSelfServe: boolean;
};

export class ProcedureDetailService extends BaseApplicationService<ProcedureContextProps> {
  state = {
    isSubmitting: false,
    resourceLoadState: ResourceLoadingState.noPermission(),
    tabState: TabState.initialize(),
    procedure: undefined as ProcedureEntity | undefined,
    customPathSettings: {},
    orgSlug: "",
    orgWithUsingFeature: undefined,
    procMsts: undefined,
    groups: undefined,
    hasCustomUIDoorPage: false,
    switchTab: this.switchTab.bind(this),
    resetState: this.resetState.bind(this),
    onCopyCreate: this.onCopyCreate.bind(this),

    onExportProcedure: this.onExportProcedure.bind(this),
    onPublish: this.onPublish.bind(this),
    onUnPublish: this.onUnPublish.bind(this),
    onProcedureDelete: this.onProcedureDelete.bind(this),
    onGroupAdded: this.onGroupAdded.bind(this),
    onDraftUpdate: this.onDraftUpdate.bind(this),
    onGrafferManagedChanged: this.onGrafferManagedChanged.bind(this),
    onProcedureUpdate: this.onProcedureUpdate.bind(this),
    onUpdateCustomerDlConfig: this.onUpdateCustomerDlConfig.bind(this),
    onArchive: this.onArchive.bind(this),
    onUnarchive: this.onUnarchive.bind(this),

    fetchProcMstBySmartApply: this.fetchProcMstBySmartApply.bind(this),
    onPublishSettingUpdate: this.onPublishSettingUpdate.bind(this),
    onReturnToDraft: this.onReturnToDraft.bind(this),
    onUpdateProcMaster: this.onUpdateProcMaster.bind(this),
    onResumeAccepting: this.onResumeAccepting.bind(this),
    onPauseAccepting: this.onPauseAccepting.bind(this),
    isOrgSelfServe: false,
  };

  async resetState(
    id: string,
    tab?: string,
    option?: { orgIdFromPath: string }, // URLのパスパラメータから取得したorgIdと手続きのorgIdが一致するかどうかをチェックする
  ) {
    this.setState({
      resourceLoadState: ResourceLoadingState.isLoading(),
    });

    try {
      const { data: proc } = await apiClient.fetchProcedure(id);
      if (option?.orgIdFromPath && proc.orgId !== option.orgIdFromPath) {
        this.setState({
          resourceLoadState: ResourceLoadingState.isLoadFailed(),
          procedure: undefined,
        });
        return;
      }

      this.setState({
        tabState: TabState.isValid(tab)
          ? TabState.from(tab)
          : TabState.initialize(),
        resourceLoadState: ResourceLoadingState.hasPermission(),
        procedure: proc,
      });
      await Promise.all([
        this.fetchHasPresentation(proc.id),
        this.fetchOrgSlug(),
        this.getGroups(),
        this.fetchCustomPathSettings(proc.orgId, proc.id),
        this.fetchOrgProducts(proc.orgId),
      ]);
    } catch (e) {
      if (SmartApplyApiError.is403(e)) {
        this.setState({
          resourceLoadState: ResourceLoadingState.noPermission(),
          procedure: undefined,
        });
        return;
      }
      this.setState({
        resourceLoadState: ResourceLoadingState.isLoadFailed(),
      });
      reportUnexpectedError(e);
    }
  }

  async onExportProcedure(id: string): Promise<boolean> {
    this.setIsSubmitting(true);
    try {
      const { data } = await apiClient.exportProcedureSettings(id);
      const blob = new Blob([JSON.stringify(data)], {
        type: "application/json",
      });
      FileSaver.saveAs(blob, `${data.transferProcedure.name}.json`);
      return true;
    } catch (e) {
      reportUnexpectedError(e);
      return false;
    } finally {
      this.setIsSubmitting(false);
    }
  }

  async onPublish(id: string) {
    this.setIsSubmitting(true);
    try {
      const { data: proc } = await apiClient.publishProcedure(id);
      this.setState({ procedure: proc });
    } catch (e) {
      reportUnexpectedError(e);
    } finally {
      this.setIsSubmitting(false);
    }
  }

  async onProcedureUpdate(proc: ProcedureType, customPath?: string) {
    try {
      await apiClient.updateProcedure(proc.id, {
        ...proc.draft,
        name: proc.name,
        summary: proc.summary,
        detailURL: proc.detailURL,
        descriptionMarkdown: proc.descriptionMarkdown,
        publishSetting: proc.publishSetting,
        isExcludeFromSearch: proc.isExcludeFromSearch,
        isSkipArrivalMail: proc.isSkipArrivalMail,
        isGrafferManaged: proc.isGrafferManaged,
        formAccessRestriction: proc.formAccessRestriction,
        isLinkTopPage: proc.isLinkTopPage,
        customerDlConfig: proc.customerDlConfig,
        updatedAt: proc.updatedAt,
        customPath: customPath,
      });
      await this.resetState(this.procedure.id, this.state.tabState.getValue());
      toast.success("手続き情報を更新しました。");
    } catch (e) {
      if (SmartApplyApiError.isVersionOutdated(e)) {
        toast.error(
          "手続きの更新に失敗しました。画面を更新して再度実行してください。",
        );
        return;
      }
      if (SmartApplyApiError.customUrlExists(e)) {
        toast.error("入力されたURLはすでに利用されています。");
        return;
      }
      if (SmartApplyApiError.notFoundFormulaUsedInPayment(e)) {
        toast.error(
          "決済設定で無効な数式IDが指定されています。設定内容をご確認ください。",
        );
        return;
      }
      toast.error("手続きの更新に失敗しました。");
      reportUnexpectedError(e);
    }
  }
  async onProcedureGrafferManagedUpdate(proc: {
    id: string;
    isGrafferManaged: boolean;
    updatedAt: Date;
  }) {
    try {
      await apiClient.updateGrafferManaged(proc.id, {
        isGrafferManaged: proc.isGrafferManaged,
        updatedAt: proc.updatedAt,
      });
      await this.resetState(this.procedure.id, this.state.tabState.getValue());
      toast.success("手続き情報を更新しました。");
    } catch (e) {
      if (SmartApplyApiError.isVersionOutdated(e)) {
        toast.error(
          "手続きの更新に失敗しました。画面を更新して再度実行してください。",
        );
        return;
      }
      if (SmartApplyApiError.customUrlExists(e)) {
        toast.error("入力されたURLはすでに利用されています。");
        return;
      }
      if (SmartApplyApiError.isErrorCode(e, "bad_request")) {
        toast.error("手続きにテナントが設定できない項目が含まれています。");
        return;
      }
      toast.error("手続きの更新に失敗しました。");
      reportUnexpectedError(e);
    }
  }

  async onDraftUpdate(detail: ProcedureDetailType) {
    const proc: ProcedureEntity = this.state.procedure!;
    this.onProcedureUpdate({
      ...proc,
      draft: detail,
    });
  }

  async onGrafferManagedChanged(isGrafferManaged: boolean) {
    const procedure: ProcedureEntity = this.state.procedure!;
    this.onProcedureGrafferManagedUpdate({
      ...procedure,
      isGrafferManaged: isGrafferManaged,
    });
  }

  async onCopyCreate(newName: string): Promise<ProcedureEntity> {
    try {
      const { data } = await apiClient.copyProcedure(
        this.procedure.id,
        newName,
      );
      return data;
    } catch (e) {
      reportUnexpectedError(e);
      throw e;
    }
  }

  async onGroupAdded(groupId: string) {
    const procedure: ProcedureEntity = this.state.procedure!;
    try {
      await apiClient.addGroupToProcedure(procedure.id, groupId);
      await this.resetState(procedure.id);
      toast.success("管理グループを設定しました。");
    } catch (e) {
      reportUnexpectedError(e);
    }
  }

  async onProcedureDelete() {
    const procedure: ProcedureEntity = this.state.procedure!;
    try {
      await apiClient.deleteProcedure(procedure.id);
      toast.success("手続きを削除しました。");
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("手続きの削除に失敗しました。");
    }
  }

  private async fetchOrgSlug() {
    const procedure: ProcedureEntity = this.state.procedure!;
    try {
      const { data: org } =
        await tenantManagementClient.fetchOrgWithUsingFeature(procedure.orgId);
      this.setState({
        orgSlug: org.getValue().slug,
        orgWithUsingFeature: org,
      });
    } catch (e) {
      toast.error("テナント情報の取得に失敗しました。");
      reportUnexpectedError(e);
    }
  }

  switchTab(tabState: TabState) {
    this.history.push(rebuildQuery({ tab: tabState.getValue() }));
    this.setState({
      tabState: tabState,
    });
  }

  setIsSubmitting(isSubmitting: boolean) {
    this.setState({
      isSubmitting: isSubmitting,
    });
  }

  private get procedure(): ProcedureEntity {
    return this.state.procedure!;
  }

  async fetchProcMstBySmartApply(smartApplyId: string, orgId: string) {
    try {
      const { data: procs } = await apiClient.procManagement.listProcs({
        organizationId: orgId,
        smartApplyId: smartApplyId,
        // group id is unnecessary for service provider account
      });
      this.setState({
        procMsts: procs.items,
      });
    } catch (e) {
      reportProcManagementRelatedError(e);
      if (isNotFoundError(e)) {
        toast.error(`手続きマスタの取得に失敗しました。`);
      } else if (isPermissionError(e)) {
        toast.error(`手続きマスタを取得する権限がありません。`);
      } else {
        toast.error(
          `手続きマスタの取得に失敗しました。電波状況のよい場所でリロードしてください。`,
        );
      }
    }
  }

  async onUpdateProcMaster(procMstId: string) {
    try {
      const procedure = this.state.procedure;
      if (!procedure) {
        return;
      }
      let currentPath: string | undefined;
      const res = await runGqlQueryV2(() =>
        gqlClient.currentCustomPaths(
          { orgId: procedure.orgId, procedureId: procedure.id },
          gqlHeader("q"),
        ),
      );
      if (res.error) {
        throw res.error;
      }
      if (res.data) {
        currentPath = res.data.currentCustomPaths.currentCustomPath?.customPath;
      }
      const { data: proc } = await apiClient.procManagement.find(procMstId);
      const onLineInfo = proc.onlineInfo;
      const rejectUpdate =
        proc.contentStatus === "PUBLISHED" && !proc.acceptAppOnline;
      if (rejectUpdate) {
        toast.error(
          `対象の手続きマスタは電子申請を受付けない状態で公開されているため、この手続きを掲載することはできません。`,
        );
        return;
      }
      proc.onlineInfo = {
        ...onLineInfo,
        applicants: onLineInfo ? onLineInfo.applicants : [],
        grafferSmartApplyInfo: {
          procedureId: procedure.id,
          procedureName: procedure.name,
          procedurePath: buildSmartApplyProcedureUrlPath(
            procedure.id,
            this.state.orgSlug,
            currentPath,
          ),
        },
      };
      proc.acceptAppOnline = true;
      const { data: newProc } = await apiClient.procManagement.update(proc);
      this.setState({
        // 手続きマスタ:スマート申請 = 1:1という前提
        procMsts: [newProc],
      });
      toast.success(`この手続きのリンクを手続きマスタに掲載しました。`);
    } catch (e) {
      // ありうる操作なので、reportしない
      if (isSmartApplyAccessRestrictionError(e)) {
        toast.error(
          "パスコード設定を無効化した状態で一度保存したあとで、再度、手続きマスタを選択してください。",
        );
        return;
      }

      reportProcManagementRelatedError(e);
      if (isNotFoundError(e)) {
        toast.error(`手続きマスタの更新に失敗しました。`);
      } else if (isPermissionError(e)) {
        toast.error(`手続きマスタを更新する権限がありません。`);
      } else {
        toast.error(
          `手続きマスタ一覧の更新に失敗しました。電波状況のよい場所でリロードしてください。`,
        );
      }
    }
  }

  private async fetchHasPresentation(id: string) {
    try {
      const { data } = await apiClient.fetchProcedurePresentation(id);
      const presentation = ProcedurePresentation.fromJSON(data);
      this.setState({
        hasCustomUIDoorPage: !!presentation.doorPageMarkdown,
      });
    } catch (e) {
      if (!SmartApplyApiError.is404(e)) {
        reportAsyncError(e);
      }
    }
  }

  /**
   * groupの取得
   */
  private async getGroups() {
    if (!this.state.procedure?.groupIds.length) {
      return;
    }
    // 現状は1手続き1グループだが複数グループ設定できるようなった場合は複数グループを取得するAPIにする
    try {
      const { data: groups } = await tenantManagementClient.fetchGroupById(
        this.state.procedure?.groupIds[0],
      );
      this.setState({
        groups: [groups],
      });
    } catch (e) {
      reportUnexpectedError(e);
    }
    // 現状1手続き1グループなので複数グループが紐付いていた場合sentryになげる
    if (1 < this.state.procedure?.groupIds.length) {
      const error = new Error(
        `Multiple IDs of ${this.state.procedure?.groupIds.join(", ")} are set`,
      );
      reportUnexpectedError(error);
    }
  }

  /**
   * 公開設定
   */
  async onPublishSettingUpdate(
    setting: PublishSetting,
    withPublish: boolean,
    isPublishNow: boolean,
    isStartAcceptAtPublish: boolean,
  ) {
    this.setIsSubmitting(true);
    try {
      const { data: proc } = await apiClient.updateProcedurePublishSetting(
        this.state.procedure!.id,
        setting,
        isPublishNow,
        isStartAcceptAtPublish,
      );
      if (withPublish) {
        const { data: proc } = await apiClient.publishProcedure(
          this.state.procedure!.id,
        );
        this.setState({ procedure: proc });
        toast.success("手続きの設定を確定しました。");
      } else {
        this.setState({ procedure: proc });
        toast.success("公開・受付日時を更新しました。");
      }
    } catch (e) {
      if (SmartApplyApiError.isVersionOutdated(e)) {
        toast.error(
          "手続きの更新に失敗しました。画面を更新して再度実行してください。",
        );
        return;
      }
      if (SmartApplyApiError.isRetryable(e)) {
        ToastMessage.retryable().show();
        return;
      }
      ToastMessage.enexpectedError().show();
      reportUnexpectedError(e);
    } finally {
      this.setIsSubmitting(false);
    }
  }

  // 下書きに戻す
  async onReturnToDraft() {
    try {
      const { data: proc } = await apiClient.returnToDraftProcedure(
        this.state.procedure!.id,
      );
      this.setState({ procedure: proc });
      toast.success("下書きに戻しました。");
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("下書きへの更新に失敗しました。");
    }
  }

  /**
   * 申請を再開する
   */
  async onResumeAccepting(id: string) {
    this.setIsSubmitting(true);
    try {
      const { data: proc } = await apiClient.resumeAcceptingProcedure(id);
      this.setState({ procedure: proc });
      toast.success("受付を再開しました。");
    } catch (e) {
      if (SmartApplyApiError.isRetryable(e)) {
        ToastMessage.retryable().show();
        return;
      }
      ToastMessage.enexpectedError().show();
      reportAsyncError(e);
    } finally {
      this.setIsSubmitting(false);
    }
  }

  /**
   * 申請を一時停止する
   */
  async onPauseAccepting(id: string) {
    this.setIsSubmitting(true);
    try {
      const { data: proc } = await apiClient.pauseAcceptingProcedure(id);
      this.setState({ procedure: proc });
      toast.success("受付を一時停止しました。");
    } catch (e) {
      if (SmartApplyApiError.isRetryable(e)) {
        ToastMessage.retryable().show();
        return;
      }
      ToastMessage.enexpectedError().show();
      reportAsyncError(e);
    } finally {
      this.setIsSubmitting(false);
    }
  }

  /**
   * 申請を終了する
   */
  async onUnPublish(id: string) {
    this.setIsSubmitting(true);
    try {
      const { data: proc } = await apiClient.unPublishProcedure(id);
      this.setState({ procedure: proc });
      toast.success("手続きの公開を終了しました。");
    } catch (e) {
      if (SmartApplyApiError.isRetryable(e)) {
        ToastMessage.retryable().show();
        return;
      }
      ToastMessage.enexpectedError().show();
      reportAsyncError(e);
    } finally {
      this.setIsSubmitting(false);
    }
  }

  async onUpdateCustomerDlConfig(
    customerDlConfig: ProcedureEntity["customerDlConfig"],
  ) {
    const procedure: ProcedureEntity = this.state.procedure!;
    this.onProcedureUpdate({
      ...procedure,
      customerDlConfig,
    });
  }

  async onArchive() {
    const procedure: ProcedureEntity = this.state.procedure!;
    try {
      const updatedAtUnixTime = new Date(procedure.updatedAt).getTime() / 1000;
      const result = await runGqlQueryV2(() =>
        gqlClient.ArchiveProcedure(
          {
            procedureId: procedure.id,
            previousUpdatedAtUnixTime: updatedAtUnixTime,
          },
          gqlHeader("m"),
        ),
      );
      if (result.error) {
        if (
          result.matchError(400, "bad_request.not_complete_all_applications")
        ) {
          toast.error("手続きに未完了の申請があるため、アーカイブできません。");
          return;
        }
        throw result.error.rawError;
      }
      toast.success("手続きをアーカイブしました。");
      const { data: proc } = await apiClient.fetchProcedure(procedure.id);
      this.setState({ procedure: proc });
    } catch (e) {
      reportAsyncError(e);
      toast.error("手続きのアーカイブに失敗しました。");
      return;
    }
  }

  async onUnarchive() {
    const procedure: ProcedureEntity = this.state.procedure!;
    try {
      const updatedAtUnixTime = new Date(procedure.updatedAt).getTime() / 1000;
      const result = await runGqlQueryV2(() =>
        gqlClient.UnarchiveProcedure(
          {
            procedureId: procedure.id,
            previousUpdatedAtUnixTime: updatedAtUnixTime,
          },
          gqlHeader("m"),
        ),
      );
      if (result.error) {
        throw result.error.rawError;
      }
      toast.success("手続きを復元しました。");
      const { data: proc } = await apiClient.fetchProcedure(procedure.id);
      this.setState({ procedure: proc });
    } catch (e) {
      reportAsyncError(e);
      toast.error("手続きの復元に失敗しました。");
      return;
    }
  }

  private async fetchCustomPathSettings(orgId: string, procedureId: string) {
    try {
      const res = await runGqlQueryV2(() =>
        gqlClient.currentCustomPaths(
          { orgId: orgId, procedureId: procedureId },
          gqlHeader("q"),
        ),
      );
      if (res.error) {
        throw res.error.rawError;
      }
      if (res.data) {
        const currentPaths = res.data.currentCustomPaths;
        this.setState({
          customPathSettings: {
            currentPath: currentPaths.currentCustomPath?.customPath,
            reserved: currentPaths.nextCustomPath?.customPath
              ? {
                  path: currentPaths.nextCustomPath.customPath,
                  startDatetime: currentPaths.nextCustomPath?.startUnixtime
                    ? new Date(
                        currentPaths.nextCustomPath?.startUnixtime * 1000,
                      )
                    : undefined,
                }
              : undefined,
          },
        });
        return;
      }
      throw new Error(
        `failed to fetch custom paths for orgId: ${orgId} and procedureId: ${procedureId}`,
      );
    } catch (e) {
      toast.error("URLのカスタマイズ情報の取得に失敗しました。");
      reportUnexpectedError(e);
    }
  }

  private async fetchOrgProducts(orgId: string) {
    const res = await client.runGqlQueryV2(() =>
      client.gqlClient.allOrgProductsByOrgId({ orgId }, client.gqlHeader("q")),
    );
    if (res.error) {
      toast.error("スマート申請の製品情報の取得に失敗しました。");
      return;
    }
    this.setState({
      isOrgSelfServe: res.data?.allOrgProductsByOrgId.some(
        ({ productCode, plan }) =>
          productCode === "SmartApply" && plan === "SelfServe",
      ),
    });
  }
}
